import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SupportWidget — floating "Ask Sage Support" panel, bottom-right on every page.
 * Public. Uses Claude Sonnet with a support-specific persona. Cost tracked on
 * the Ledger via /api/sage/support/meter.
 */
export default function SupportWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streamText, setStreamText] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [mode, setMode] = useState("chat"); // "chat" | "ticket"
  const [ticketEmail, setTicketEmail] = useState("");
  const [ticketBody, setTicketBody] = useState("");
  const [ticketOut, setTicketOut] = useState(null);
  const [filing, setFiling] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [streamText, messages]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const q = input.trim();
    if (!q || streaming) return;
    setInput("");
    const userMsg = { role: "user", content: q };
    setMessages((prev) => [...prev, userMsg]);
    setStreaming(true);
    setStreamText("");
    setReceipt(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/sage/support/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: q }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const chunk = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const ev = chunk.match(/^event:\s*(.*)$/m)?.[1]?.trim();
          const dm = chunk.match(/^data:\s*(.*)$/m)?.[1];
          if (!ev || !dm) continue;
          let d = {};
          try { d = JSON.parse(dm); } catch {}
          if (ev === "meta") { if (!sessionId) setSessionId(d.session_id); }
          else if (ev === "token" && d.text) { acc += d.text; setStreamText(acc); }
          else if (ev === "receipt") setReceipt(d);
        }
      }
      setMessages((prev) => [...prev, { role: "assistant", content: acc }]);
      setStreamText("");
    } catch (e2) {
      setMessages((prev) => [...prev, { role: "assistant", content: `[support error: ${e2.message}]` }]);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {/* Floating toggle button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="support-widget-open"
          className="fixed bottom-4 right-4 z-40 border border-sage-accent bg-sage-bg text-sage-accent w-20 h-20 text-[10px] uppercase tracking-[0.1em] leading-tight hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150 shadow-lg flex items-center justify-center text-center px-2"
        >
          Ask Sage
          <br />
          Support
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="support-widget-panel"
          className="fixed bottom-4 right-4 z-40 w-[92vw] sm:w-[380px] max-h-[70vh] bg-sage-bg border border-sage-border shadow-2xl flex flex-col"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-sage-border">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-faint">
              {mode === "chat" ? "Sage Support · AI helper" : "Sage Support · file a ticket"}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setMode(mode === "chat" ? "ticket" : "chat"); setTicketOut(null); }}
                data-testid="support-widget-mode-toggle"
                className="text-[10px] uppercase tracking-[0.1em] text-sage-accent hover:text-sage-text"
              >
                {mode === "chat" ? "file ticket →" : "← back to chat"}
              </button>
              <button
                onClick={() => setOpen(false)}
                data-testid="support-widget-close"
                className="text-xs text-sage-faint hover:text-sage-text"
              >
                close ×
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-auto p-3 space-y-3 min-h-[200px]">
            {mode === "ticket" ? (
              <div className="space-y-3">
                <div className="text-xs text-sage-faint leading-relaxed sage-prose">
                  Chat didn't solve it? File a formal ticket. It gets an instant answer from a fresh helper, and if that still doesn't resolve it, it escalates through the loop — full three-tier system.
                </div>
                <input
                  data-testid="support-ticket-email"
                  type="email"
                  value={ticketEmail}
                  onChange={(e) => setTicketEmail(e.target.value)}
                  placeholder="your email (optional but recommended)"
                  className="w-full bg-sage-bg border border-sage-border px-2 py-1.5 text-xs text-sage-text placeholder-sage-faint focus:outline-none focus:border-sage-accent"
                />
                <textarea
                  data-testid="support-ticket-body"
                  value={ticketBody}
                  onChange={(e) => setTicketBody(e.target.value)}
                  placeholder="Describe the issue in your own words. Minimum a sentence."
                  rows={5}
                  className="w-full bg-sage-bg border border-sage-border px-2 py-1.5 text-xs text-sage-text placeholder-sage-faint focus:outline-none focus:border-sage-accent resize-y"
                />
                <button
                  data-testid="support-ticket-submit"
                  disabled={filing || ticketBody.trim().length < 5}
                  onClick={async () => {
                    setFiling(true);
                    setTicketOut(null);
                    try {
                      const r = await api.supportSubmit({
                        issue: ticketBody.trim(),
                        session_id: sessionId || undefined,
                        email: ticketEmail.trim() || undefined,
                      });
                      setTicketOut(r);
                      setTicketBody("");
                    } catch (e) {
                      setTicketOut({ error: e.message });
                    } finally {
                      setFiling(false);
                    }
                  }}
                  className="w-full text-[10px] uppercase tracking-[0.1em] border border-sage-accent text-sage-accent px-3 py-2 hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150 disabled:opacity-40"
                >
                  {filing ? "filing…" : "file the ticket →"}
                </button>
                {ticketOut && !ticketOut.error && (
                  <div className="border border-sage-accent p-3 text-xs text-sage-text sage-prose space-y-2" data-testid="support-ticket-result">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-sage-accent">ticket filed · {ticketOut.tier}</div>
                    <div className="whitespace-pre-wrap">{ticketOut.response}</div>
                    <div className="text-[10px] text-sage-faint pt-2 border-t border-sage-border tabular-nums">
                      ticket id · {ticketOut.ticket_id?.slice(0, 8)} · next check · {ticketOut.next_check_at?.slice(0, 10)}
                    </div>
                  </div>
                )}
                {ticketOut?.error && (
                  <div className="text-xs text-sage-warn" data-testid="support-ticket-error">
                    Could not file: {ticketOut.error}
                  </div>
                )}
              </div>
            ) : (
              <>
            {messages.length === 0 && !streaming && (
              <div className="text-xs text-sage-faint italic leading-relaxed">
                Ask about how Sage works — memberships, refunds, the Ledger, the Fill Bar, the Founding Papers. I answer from Sage's own docs. If I don't know, I'll say so and route you to the Founder.
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`support-msg-${i}`}
                className={`text-sm leading-relaxed sage-prose ${m.role === "user" ? "text-sage-text" : "text-sage-muted"}`}
              >
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">
                  {m.role === "user" ? "you" : "sage support"}
                </div>
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
            ))}
            {streamText && (
              <div className="text-sm text-sage-muted leading-relaxed sage-prose" data-testid="support-streaming">
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-accent mb-1">sage support · streaming</div>
                <div className="whitespace-pre-wrap">{streamText}<span className="inline-block w-2 h-4 bg-sage-accent animate-pulse ml-1"/></div>
              </div>
            )}
            {receipt && (
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint pt-2 border-t border-sage-border tabular-nums" data-testid="support-receipt">
                {receipt.duration_ms/1000}s · ${receipt.cost_usd?.toFixed(6) || "0.000000"}
              </div>
            )}
              </>
            )}
          </div>

          {mode === "chat" && (
          <form onSubmit={submit} className="border-t border-sage-border p-2 flex gap-2">
            <input
              data-testid="support-widget-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={streaming ? "…" : "Ask a question"}
              disabled={streaming}
              className="flex-1 bg-sage-bg border border-sage-border px-2 py-1.5 text-xs text-sage-text placeholder-sage-faint focus:outline-none focus:border-sage-accent"
            />
            <button
              type="submit"
              data-testid="support-widget-send"
              disabled={streaming || !input.trim()}
              className="text-[10px] uppercase tracking-[0.1em] border border-sage-accent text-sage-accent px-3 py-1.5 hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150 disabled:opacity-40"
            >
              {streaming ? "…" : "Send →"}
            </button>
          </form>
          )}
        </div>
      )}
    </>
  );
}
